/**
 * Saved OpenAPI specifications.
 *
 * The plan limit is displayed from the server response, and the server enforces
 * it as well, so the UI cannot be used to exceed it.
 */

import { useState } from 'react';
import type { Entitlements } from '@core/types';
import { Alert, Badge, EmptyState, Field, Spinner } from '../components/ui';
import { PageHead } from '../components/Layout';
import { api, ApiError, type SpecSummary } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatDateTime, formatTitleCase } from '../lib/format';

interface DraftState {
  id: string | null;
  name: string;
  description: string;
  content: string;
}

const EMPTY_DRAFT: DraftState = { id: null, name: '', description: '', content: '' };

const SAMPLE_SPEC = `openapi: 3.0.3
info:
  title: Example Orders API
  version: 1.0.0
servers:
  - url: https://api.example.com/v1
paths:
  /orders:
    post:
      operationId: createOrder
      parameters:
        - name: X-Request-Id
          in: header
          required: true
          schema:
            type: string
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
              required: [amount, currency]
              properties:
                amount:
                  type: integer
                currency:
                  type: string
                  enum: [usd, eur, gbp]
                reference:
                  type: string
      responses:
        '201':
          description: Created
          content:
            application/json:
              schema:
                type: object
                required: [id]
                properties:
                  id:
                    type: string
`;

export function SpecsPage({ entitlements }: { entitlements: Entitlements }) {
  const state = useAsync(async (token) => api.listSpecs(token), []);
  const { getToken } = useSession();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const specs = state.data?.specs ?? [];
  const limit = state.data?.limit ?? entitlements.maxSavedSpecs;
  const atLimit = specs.length >= limit;

  async function save() {
    setBusy(true);
    setError(null);
    setWarnings([]);
    try {
      const token = await getToken();
      if (draft.id) {
        const result = await api.updateSpec(token, draft.id, {
          name: draft.name,
          description: draft.description || null,
          ...(draft.content.trim().length > 0 ? { content: draft.content } : {}),
        });
        if (result.spec) setWarnings([]);
      } else {
        const result = await api.createSpec(token, {
          name: draft.name,
          description: draft.description || null,
          content: draft.content,
        });
        setWarnings(result.warnings ?? []);
      }
      setDraft(EMPTY_DRAFT);
      state.reload();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? `${caught.message}${caught.hint ? ` ${caught.hint}` : ''}`
          : 'The specification could not be saved.',
      );
    } finally {
      setBusy(false);
    }
  }

  // The token is fetched fresh for each mutation so a refreshed session is used.

  async function remove(id: string) {
    if (
      !window.confirm('Delete this specification? Diagnoses that referenced it keep their report.')
    )
      return;
    setBusy(true);
    try {
      const token = await getToken();
      await api.deleteSpec(token, id);
      if (draft.id === id) setDraft(EMPTY_DRAFT);
      state.reload();
    } catch {
      setError('The specification could not be deleted.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="Saved OpenAPI specifications"
        description="Save an OpenAPI 3.x document once and reuse it to validate requests during every diagnosis. Only structured data is stored; the uploaded file is discarded."
        actions={
          <span className="row">
            <Badge kind={atLimit ? 'error' : 'default'}>
              {specs.length}/{limit} saved
            </Badge>
            <Badge kind={entitlements.openApiValidation === 'full' ? 'pro' : 'default'}>
              {entitlements.openApiValidation === 'full' ? 'Full validation' : 'Structural only'}
            </Badge>
          </span>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}

      {warnings.length > 0 ? (
        <Alert tone="warning" title="Parsed with warnings">
          <ul className="plain-list">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="card">
        <h2>{draft.id ? 'Replace or rename specification' : 'Add a specification'}</h2>

        <Field label="Name" htmlFor="spec-name">
          <input
            id="spec-name"
            type="text"
            value={draft.name}
            maxLength={200}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            placeholder="Payments API (production)"
          />
        </Field>

        <Field label="Description (optional)" htmlFor="spec-description">
          <input
            id="spec-description"
            type="text"
            value={draft.description}
            maxLength={1000}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            placeholder="What this specification covers"
          />
        </Field>

        <Field
          label="Document"
          htmlFor="spec-content"
          help="OpenAPI 3.x as JSON or YAML. Swagger 2.0 is rejected with a clear message. External $ref values are ignored, never fetched."
        >
          <textarea
            id="spec-content"
            rows={14}
            value={draft.content}
            spellCheck={false}
            onChange={(event) => setDraft({ ...draft, content: event.target.value })}
            placeholder="paste an OpenAPI 3.x document here"
          />
        </Field>

        <div className="row">
          <button
            type="button"
            className="button button--primary"
            disabled={
              busy ||
              draft.name.trim().length === 0 ||
              (!draft.id && draft.content.trim().length === 0) ||
              (!draft.id && atLimit)
            }
            onClick={save}
          >
            {busy ? <Spinner /> : null}
            {draft.id ? 'Save changes' : 'Save specification'}
          </button>
          <button
            type="button"
            className="button--ghost"
            disabled={busy}
            onClick={() => setDraft({ ...EMPTY_DRAFT, content: SAMPLE_SPEC })}
          >
            Load a sample document
          </button>
          {draft.id ? (
            <button
              type="button"
              className="button--ghost"
              onClick={() => setDraft(EMPTY_DRAFT)}
              disabled={busy}
            >
              Cancel editing
            </button>
          ) : null}
        </div>

        {!draft.id && atLimit ? (
          <Alert tone="warning" title="Saved specification limit reached">
            The {entitlements.displayName} plan allows {limit} saved specification
            {limit === 1 ? '' : 's'}. Delete one, or upgrade to Pro for up to 10.
          </Alert>
        ) : null}
      </div>

      {state.loading ? (
        <div className="card row">
          <Spinner label="Loading specifications" /> <span>Loading specifications…</span>
        </div>
      ) : null}

      {state.data && specs.length === 0 ? (
        <EmptyState title="No specifications saved yet">
          <p>Add one above to enable contract validation during a diagnosis.</p>
        </EmptyState>
      ) : null}

      {specs.length > 0 ? (
        <div className="card card--flush">
          <div className="table-wrap">
            <table>
              <caption className="sr-only">Saved OpenAPI specifications</caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Document</th>
                  <th scope="col">Operations</th>
                  <th scope="col">Updated</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {specs.map((spec: SpecSummary) => (
                  <tr key={spec.id}>
                    <td>
                      <div>{spec.name}</div>
                      {spec.description ? (
                        <div className="small faint">{spec.description}</div>
                      ) : null}
                    </td>
                    <td className="small">
                      <div>
                        <code>{spec.title ?? '(untitled)'}</code>
                      </div>
                      <div className="faint">
                        OpenAPI {spec.openapi} · {formatTitleCase(spec.sourceFormat)}
                        {spec.version ? ` · v${spec.version}` : ''}
                      </div>
                    </td>
                    <td className="nowrap">{spec.operationCount}</td>
                    <td className="nowrap small faint">{formatDateTime(spec.updatedAt)}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <button
                          type="button"
                          className="button--small"
                          onClick={() =>
                            setDraft({
                              id: spec.id,
                              name: spec.name,
                              description: spec.description ?? '',
                              content: '',
                            })
                          }
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="button--danger button--small"
                          disabled={busy}
                          onClick={() => remove(spec.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}
